import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useCopyToClipboard } from "usehooks-ts"
import CopyIcon from "@/components/icons/copy"
import { toast } from "sonner"
import { format } from "date-fns"
import { useTransactions } from "@/api/transactions"
import { DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { TokenIcon } from "@/components/token"
import { Badge } from "@/components/ui/badge"
import NetworkIcon from "@/components/icons/network"
import Loader from "@/components/layout/loader"
import OptionsDropdownMenu from "@/components/ux/options.dropdown-menu"
import { shortenString } from "@/lib/utils"
import PaymentTypeIcon from "@/components/ux/payment-type.icon"

const TABLE_COLUMNS = 6

export default function TransactionsTable() {
  const { transactions, size, setSize, hasMore } = useTransactions({})
  const [, copy] = useCopyToClipboard()

  const handleCopy = async (hash: string) => {
    await copy(hash)
    toast.success("Copied transaction ID to clipboard")
  }

  if (!transactions) {
    return <Loader message="Loading payments..." />
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="rounded-t-md">
          <TableHead></TableHead>
          <TableHead className="pl-6">Date</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>From</TableHead>
          <TableHead>To</TableHead>
          <TableHead>Network</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {transactions.map((walletTransaction) => (
          <TableRow key={walletTransaction.id}>
            <TableCell className="px-4">
              <PaymentTypeIcon type={walletTransaction.type} />
            </TableCell>
            <TableCell className="pl-6 text-nowrap">
              {format(new Date(walletTransaction.createdAt), "MMM do, yyyy")}
            </TableCell>
            <TableCell>
              <div className="flex flex-row gap-2 items-center text-nowrap">
                {walletTransaction.transaction.token && (
                  <TokenIcon name={walletTransaction.transaction.token} />
                )}
                <div>
                  {walletTransaction.transaction.amount}{" "}
                  {walletTransaction.transaction.token}
                </div>
              </div>
            </TableCell>
            <TableCell>
              {shortenString(walletTransaction.transaction.fromAddress, 10)}
            </TableCell>
            <TableCell>
              {shortenString(walletTransaction.transaction.toAddress, 10)}
            </TableCell>
            <TableCell>
              <Badge
                variant="secondary"
                className="flex flex-row gap-2 font-normal text-sm w-fit"
              >
                <div className="w-4 h-4">
                  <NetworkIcon name={walletTransaction.transaction.network} />
                </div>
                {walletTransaction.transaction.network.substring(0, 3)}
              </Badge>
            </TableCell>
            <TableCell>
              <OptionsDropdownMenu>
                <DropdownMenuItem
                  onClick={() => handleCopy(walletTransaction.transaction.hash)}
                >
                  <CopyIcon />
                  Transaction ID
                </DropdownMenuItem>
              </OptionsDropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {transactions.length === 0 && (
          <TableRow>
            <TableCell
              colSpan={TABLE_COLUMNS}
              className="text-center text-gray-300"
            >
              No transactions found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      {hasMore && (
        <TableFooter>
          <TableRow>
            <TableCell colSpan={TABLE_COLUMNS}>
              <button
                onClick={() => setSize(size + 1)}
                className="flex flex-col w-full items-center bg-white p-3 text-center"
              >
                Load more...
              </button>
            </TableCell>
          </TableRow>
        </TableFooter>
      )}
    </Table>
  )
}
